import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { Marquee } from "@/components/Marquee";
import { IMG, MARQUEE_ITEMS } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

const values = [
  { title: "Flexible", desc: "Services and schedules shaped around your requirements — not the other way around." },
  { title: "Thorough", desc: "We focus on the details that make a space feel genuinely clean, not just the visible areas." },
  { title: "Approachable", desc: "Cleaning solutions for every budget, from everyday households to premium properties." },
  { title: "Dependable", desc: "Reliable service for homes and businesses that need cleaning they can count on." },
];

export default function About() {
  useSeo("About Us | RD Cleaning Services Perth", "RD Cleaning Services provides reliable residential and commercial cleaning solutions across Perth, WA — flexible services with attention to detail.");
  return (
    <>
      <PageHero
        eyebrow="About RD Cleaning Services"
        title="Cleaning You Can Count On"
        subtitle="RD Cleaning Services provides reliable residential and commercial cleaning solutions across Perth. We understand that every property is different, which is why we focus on flexible services, attention to detail and cleaning solutions designed around each customer's requirements."
        image={IMG.team}
      >
        <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="about-hero-quote-button">
          Get a Free Quote <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>
      <Marquee items={MARQUEE_ITEMS} />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <img src={IMG.team} alt="Professional cleaning team members in uniform" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow">Who We Are</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Professional cleaning for every home and every business.
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              From budget-conscious households and small businesses to premium homes, offices, medical
              facilities and larger commercial clients — our goal is simple: a cleaning service that works
              for you, at a standard you'll notice.
            </p>
            <ul className="mt-8 space-y-4">
              {["Residential and commercial expertise", "One-off, regular and custom cleaning plans", "Attention to detail on every visit", "Friendly, straightforward communication"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Our Values</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">How We Work</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg">
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection heading="Let's Talk About Your Space" text="Tell us what you need cleaned and we'll find a solution that fits your property, schedule and budget." />
    </>
  );
}
